import React, { useState, useEffect } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Spinner, Modal, Input, Alert, AlertDescription, Column, TableSkeleton } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { Plus, Package, CheckCircle, Edit3, Trash2, Eye, Upload, Download, Code } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { FlowDefinition, PrimitiveCapabilityRequirements, JSONSchema } from '@simcheck/shared-types';
import {
  getAllPrimitives,
  createPrimitive,
  updatePrimitive,
  deletePrimitive,
  publishPrimitive,
  unpublishPrimitive,
} from '@/services/primitiveService';
import { SchemaEditor } from '@/components/primitives/SchemaEditor';
import { CapabilityRequirementsEditor } from '@/components/flow-editor/CapabilityRequirementsEditor';

export function PrimitivesAdminPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { success, error: toastError } = useToast();
  
  const [primitives, setPrimitives] = useState<FlowDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [error, setError] = useState('');
  
  // Create Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createCategory, setCreateCategory] = useState('');
  const [createInputSchema, setCreateInputSchema] = useState<JSONSchema>({
    type: 'object',
    properties: {},
    required: [],
  });
  const [createOutputSchema, setCreateOutputSchema] = useState<JSONSchema>({
    type: 'object',
    properties: {},
    required: [],
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  
  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPrimitive, setEditPrimitive] = useState<FlowDefinition | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editInputSchema, setEditInputSchema] = useState<JSONSchema>({
    type: 'object',
    properties: {},
    required: [],
  });
  const [editOutputSchema, setEditOutputSchema] = useState<JSONSchema>({
    type: 'object',
    properties: {},
    required: [],
  });
  const [editCapabilityRequirements, setEditCapabilityRequirements] = useState<PrimitiveCapabilityRequirements>({
    required: [],
    preferred: [],
    modemCount: 1,
  });
  // AT safety policy override. Each entry is an exact-match AT command
  // string that this primitive is permitted to execute even if it would
  // otherwise be BLOCKED by the policy. See plans/at-command-safety-layer.md.
  const [editAllowBlockedCommands, setEditAllowBlockedCommands] = useState<string[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  
  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [primitiveToDelete, setPrimitiveToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Load primitives
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    loadPrimitives();
  }, [isSuperAdmin]);
  
  const loadPrimitives = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAllPrimitives();
      setPrimitives(data);
    } catch (err: any) {
      console.error('Failed to load primitives:', err);
      setError(err.message || 'Failed to load primitives');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreatePrimitive = async () => {
    if (!createName.trim()) {
      setCreateError('Primitive name is required');
      return;
    }
    if (!createCategory.trim()) {
      setCreateError('Category is required');
      return;
    }
    
    setCreateLoading(true);
    setCreateError('');
    
    try {
      await createPrimitive({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        category: createCategory.trim(),
        inputSchema: createInputSchema,
        outputSchema: createOutputSchema,
        flow: { nodes: [], edges: [] },
      });
      
      success('Primitive created successfully!');
      setIsCreateModalOpen(false);
      resetCreateForm();
      await loadPrimitives();
    } catch (err: any) {
      console.error('Failed to create primitive:', err);
      setCreateError(err.message || 'Failed to create primitive');
      toastError(err.message || 'Failed to create primitive');
    } finally {
      setCreateLoading(false);
    }
  };
  
  const handleEditClick = (primitive: FlowDefinition) => {
    setEditPrimitive(primitive);
    setEditName(primitive.name);
    setEditDescription(primitive.description || '');
    setEditCategory(primitive.category || '');
    setEditInputSchema(primitive.inputSchema || { type: 'object', properties: {}, required: [] });
    setEditOutputSchema(primitive.outputSchema || { type: 'object', properties: {}, required: [] });
    setEditCapabilityRequirements(primitive.capabilityRequirements || { required: [], preferred: [], modemCount: 1 });
    setEditAllowBlockedCommands(Array.isArray((primitive as any).allowBlockedCommands) ? (primitive as any).allowBlockedCommands : []);
    setIsEditModalOpen(true);
  };
  
  const handleEditPrimitive = async () => {
    if (!editPrimitive) return;
    if (!editName.trim()) {
      setEditError('Primitive name is required');
      return;
    }
    if (!editCategory.trim()) {
      setEditError('Category is required');
      return;
    }
    
    setEditLoading(true);
    setEditError('');
    
    try {
      await updatePrimitive(editPrimitive.id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        category: editCategory.trim(),
        inputSchema: editInputSchema,
        outputSchema: editOutputSchema,
        capabilityRequirements: editCapabilityRequirements,
        // @ts-expect-error — field kept loose until FlowDefinition type is extended
        allowBlockedCommands: editAllowBlockedCommands,
      });
      
      success('Primitive updated successfully!');
      setIsEditModalOpen(false);
      resetEditForm();
      await loadPrimitives();
    } catch (err: any) {
      console.error('Failed to update primitive:', err);
      setEditError(err.message || 'Failed to update primitive');
      toastError(err.message || 'Failed to update primitive');
    } finally {
      setEditLoading(false);
    }
  };
  
  const handlePublishToggle = async (primitive: FlowDefinition) => {
    try {
      if (primitive.isPublished) {
        await unpublishPrimitive(primitive.id);
        success(`${primitive.name} unpublished`);
      } else {
        await publishPrimitive(primitive.id);
        success(`${primitive.name} published`);
      }
      await loadPrimitives();
    } catch (err: any) {
      console.error('Failed to toggle publish status:', err);
      toastError(err.message || 'Failed to update primitive');
    }
  };
  
  const handleDeleteClick = (primitive: FlowDefinition) => {
    setPrimitiveToDelete({ id: primitive.id, name: primitive.name });
    setIsDeleteModalOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!primitiveToDelete) return;
    
    setDeleteLoading(true);
    try {
      await deletePrimitive(primitiveToDelete.id);
      success('Primitive deleted successfully');
      setIsDeleteModalOpen(false);
      setPrimitiveToDelete(null);
      await loadPrimitives();
    } catch (err: any) {
      console.error('Failed to delete primitive:', err);
      toastError(err.message || 'Failed to delete primitive');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const resetCreateForm = () => {
    setCreateName('');
    setCreateDescription('');
    setCreateCategory('');
    setCreateInputSchema({ type: 'object', properties: {}, required: [] });
    setCreateOutputSchema({ type: 'object', properties: {}, required: [] });
    setCreateError('');
  };
  
  const resetEditForm = () => {
    setEditPrimitive(null);
    setEditName('');
    setEditDescription('');
    setEditCategory('');
    setEditInputSchema({ type: 'object', properties: {}, required: [] });
    setEditOutputSchema({ type: 'object', properties: {}, required: [] });
    setEditCapabilityRequirements({ required: [], preferred: [], modemCount: 1 });
    setEditAllowBlockedCommands([]);
    setEditError('');
  };
  
  // Calculate statistics
  const stats = {
    total: primitives.length,
    published: primitives.filter(p => p.isPublished).length,
    draft: primitives.filter(p => !p.isPublished).length,
  };
  
  const filteredPrimitives = filter === 'all'
    ? primitives
    : filter === 'published'
    ? primitives.filter(p => p.isPublished)
    : primitives.filter(p => !p.isPublished);
  
  // Define table columns
  const columns: Column<FlowDefinition>[] = [
    {
      key: 'name',
      header: 'Primitive Name',
      sortable: true,
      render: (_, primitive) => (
        <div>
          <div className="font-medium text-foreground">
            {primitive.name}
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {typeof primitive.description === 'string' ? primitive.description : 'No description'}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (_, primitive) => (
        <Badge variant="secondary">
          {primitive.category || 'Uncategorized'}
        </Badge>
      ),
    },
    {
      key: 'isPublished',
      header: 'Status',
      sortable: true,
      render: (_, primitive) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Badge
            variant="secondary"
            className={`cursor-pointer hover:opacity-80 transition-opacity ${primitive.isPublished ? 'bg-status-success-bg text-status-success' : 'bg-status-warning-bg text-status-warning'}`}
            onClick={() => handlePublishToggle(primitive)}
            title={primitive.isPublished ? 'Click to unpublish' : 'Click to publish'}
          >
            {primitive.isPublished ? 'Published' : 'Draft'}
          </Badge>
        </div>
      ),
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      render: (_, primitive) => (
        <span className="text-sm font-mono">{primitive.version || 1}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, primitive) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/primitives/${primitive.id}/edit-flow`);
            }}
            title="Edit flow"
          >
            <Edit3 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(primitive);
            }}
            title="Properties"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(primitive);
            }}
            title="Delete primitive"
          >
            <Trash2 className="w-4 h-4 text-accent-red" />
          </Button>
        </div>
      ),
    },
  ];
  
  if (!isSuperAdmin) {
    return null;
  }
  
  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              Primitive Management
            </h1>
            <p className="text-body text-muted-foreground">
              Create and manage reusable primitive flows
            </p>
          </div>
          
          <Button
            variant="default"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Create Primitive
          </Button>
        </div>
        
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Loading State */}
        {loading && (
          <Card padding="lg">
            <TableSkeleton rows={5} />
          </Card>
        )}
        
        {/* Stats */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card variant="metric" padding="md">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-accent-blue" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Total Primitives</div>
                  <div className="text-h2 font-mono font-bold text-foreground">{stats.total}</div>
                </div>
              </div>
            </Card>
            
            <Card variant="metric" padding="md">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent-green" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Published</div>
                  <div className="text-h2 font-mono font-bold text-accent-green">{stats.published}</div>
                </div>
              </div>
            </Card>
            
            <Card variant="metric" padding="md">
              <div className="flex items-center gap-3">
                <Edit3 className="w-5 h-5 text-accent-orange" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Draft</div>
                  <div className="text-h2 font-mono font-bold text-accent-orange">{stats.draft}</div>
                </div>
              </div>
            </Card>
          </div>
        )}
        
        {/* Filters */}
        <div className="flex items-center gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            variant={filter === 'published' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilter('published')}
          >
            Published
          </Button>
          <Button
            variant={filter === 'draft' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilter('draft')}
          >
            Draft
          </Button>
        </div>
        
        {/* Primitives Table */}
        {!loading && (
          <Card padding="lg">
            <DataTable
              columns={columns}
              data={filteredPrimitives}
              keyExtractor={(primitive) => primitive.id}
              onRowClick={(primitive) => navigate(`/admin/primitives/${primitive.id}/edit-flow`)}
              searchable
              searchPlaceholder="Search primitives by name or category..."
              emptyMessage={
                filter === 'all'
                  ? 'Get started by creating your first primitive'
                  : `No ${filter} primitives available`
              }
            />
          </Card>
        )}
      </div>
      
      {/* Create Primitive Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetCreateForm();
        }}
        title="Create New Primitive"
        size="lg"
      >
        <div className="space-y-4">
          {createError && (
            <Alert variant="destructive">
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}
          
          <Input
            label="Primitive Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="e.g., Check Signal Strength"
            required
            fullWidth
            disabled={createLoading}
          />
          
          <Input
            label="Description"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            placeholder="Brief description of what this primitive does"
            fullWidth
            disabled={createLoading}
          />
          
          <Input
            label="Category"
            value={createCategory}
            onChange={(e) => setCreateCategory(e.target.value)}
            placeholder="e.g., Network, SMS, Messaging"
            required
            fullWidth
            disabled={createLoading}
          />
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Input Schema
            </label>
            <SchemaEditor
              value={createInputSchema}
              onChange={setCreateInputSchema}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Output Schema
            </label>
            <SchemaEditor
              value={createOutputSchema}
              onChange={setCreateOutputSchema}
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetCreateForm();
              }}
              disabled={createLoading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleCreatePrimitive}
              disabled={createLoading || !createName.trim() || !createCategory.trim()}
            >
              {createLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Create Primitive'
              )}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Edit Primitive Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          resetEditForm();
        }}
        title="Edit Primitive"
        size="lg"
      >
        <div className="space-y-4">
          {editError && (
            <Alert variant="destructive">
              <AlertDescription>{editError}</AlertDescription>
            </Alert>
          )}
          
          <Input
            label="Primitive Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="e.g., Check Signal Strength"
            required
            fullWidth
            disabled={editLoading}
          />
          
          <Input
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Brief description of what this primitive does"
            fullWidth
            disabled={editLoading}
          />
          
          <Input
            label="Category"
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value)}
            placeholder="e.g., Network, SMS, Messaging"
            required
            fullWidth
            disabled={editLoading}
          />
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Input Schema
            </label>
            <SchemaEditor
              value={editInputSchema}
              onChange={setEditInputSchema}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Output Schema
            </label>
            <SchemaEditor
              value={editOutputSchema}
              onChange={setEditOutputSchema}
            />
          </div>

          <div className="border-t border-border pt-4 mt-4">
            <CapabilityRequirementsEditor
              requirements={editCapabilityRequirements}
              onChange={setEditCapabilityRequirements}
              readOnly={editLoading}
            />
          </div>

          <div className="border-t border-border pt-4 mt-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              AT Safety Policy Overrides
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Exact-match AT commands this primitive is permitted to execute even if they
              would otherwise be BLOCKED by the safety policy. One command per line.
              <strong className="text-status-error"> Only add commands here if you are certain they are
              needed. Mistakes here can permanently brick modems.</strong>
            </p>
            <textarea
              value={editAllowBlockedCommands.join('\n')}
              onChange={(e) => {
                const lines = e.target.value
                  .split('\n')
                  .map((l) => l.trim())
                  .filter((l) => l.length > 0);
                setEditAllowBlockedCommands(lines);
              }}
              disabled={editLoading}
              rows={3}
              placeholder={'AT+CFUN=1,1'}
              className="w-full px-3 py-2 text-sm font-mono border border-border rounded bg-card text-foreground focus:ring-2 focus:ring-accent-blue focus:border-transparent disabled:opacity-50"
            />
            {editAllowBlockedCommands.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-xs text-status-warning">
                <span>⚠️ {editAllowBlockedCommands.length} override{editAllowBlockedCommands.length !== 1 ? 's' : ''} set on this primitive</span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setIsEditModalOpen(false);
                resetEditForm();
              }}
              disabled={editLoading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleEditPrimitive}
              disabled={editLoading || !editName.trim() || !editCategory.trim()}
            >
              {editLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPrimitiveToDelete(null);
        }}
        title="Delete Primitive"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-body text-muted-foreground">
            Are you sure you want to delete <strong className="text-foreground">"{primitiveToDelete?.name}"</strong>? 
            This action cannot be undone.
          </p>
          
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setPrimitiveToDelete(null);
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Primitive'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
